import { Tooltip } from '@base-ui/react/tooltip';
import type { DiscoveryBucket } from '@sdd-maturity/shared-types';

interface BucketBadgeProps {
  bucket: DiscoveryBucket;
}

// Bucket labels reflect content depth, not whether the feature was scored
// (every feature is scored end-to-end now — bucket is a quality signal):
//   SDD     = full Discovery template detected (≥4 sections + BDD ACs)
//   Partial = some structured content but incomplete
//   Stub    = Rally-template placeholder content (<500 chars or ≤1 section)
const BUCKET_CONFIG: Record<DiscoveryBucket, { label: string; tooltip: string; className: string }> = {
  'sdd-discovery': {
    label: 'SDD',
    tooltip: 'Full Discovery template detected — problem statement, scope, and BDD acceptance criteria all present.',
    className: 'bucket-badge bucket-badge-sdd',
  },
  'partial-sdd': {
    label: 'Partial',
    tooltip: 'Some structured Discovery content present but missing key sections.',
    className: 'bucket-badge bucket-badge-partial',
  },
  'not-sdd': {
    label: 'Stub',
    tooltip: 'Description is mostly Rally-template placeholder content (under 500 chars or only one section). Still scored, but expect a low rubric score.',
    className: 'bucket-badge bucket-badge-stub',
  },
};

/**
 * Badge displaying discovery bucket classification.
 * AC-9.1, AC-8.3 — used in FeatureTable and FeatureDrillDown.
 */
export function BucketBadge({ bucket }: BucketBadgeProps) {
  const config = BUCKET_CONFIG[bucket];
  return (
    <Tooltip.Provider delay={150}>
      <Tooltip.Root>
        <Tooltip.Trigger
          render={
            <span
              data-testid={`bucket-badge-${bucket}`}
              role="status"
              aria-label={`Classification: ${config.label}`}
              className={config.className}
            />
          }
        >
          {config.label}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Positioner sideOffset={6} style={{ zIndex: 200 }}>
            <Tooltip.Popup
              role="tooltip"
              style={{
                maxWidth: '16rem',
                padding: 'var(--genai-space-sm, 0.5rem) var(--genai-space-md, 0.75rem)',
                borderRadius: 'var(--genai-radius-md, 0.375rem)',
                border: '1px solid var(--genai-sem-border-strong)',
                backgroundColor: 'var(--genai-sem-surface-raised)',
                color: 'var(--genai-sem-text-primary)',
                fontSize: 'var(--genai-text-xs, 0.75rem)',
                fontWeight: 400,
                lineHeight: 1.4,
                boxShadow: 'var(--genai-shadow-md, 0 2px 8px rgba(0, 0, 0, 0.15))',
              }}
            >
              {config.tooltip}
            </Tooltip.Popup>
          </Tooltip.Positioner>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
